import { Grid } from "@mui/material";
import { useEvents } from "api/events";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { memo, useState } from "react";
import { SchemaAttribute, SchemaFields } from "types/customEvents";

interface Props {
  event?: string;
  isEditMode?: boolean;
}

export const ExternalEvent = memo(({ event, isEditMode }: Props) => {
  const [customEventsSearch, setSearchEvent] = useState<string>();
  const { message } = useMessage();

  const { useInfiniteCustomEventsSchemaList, useCustomEventSchemaDetails } =
    useEvents();

  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(event);

  const {
    data: infiniteCustomEventSchemaList,
    size: customEventSchemaSize,
    setSize: setCustomEventSchemaSize,
    isValidating,
  } = useInfiniteCustomEventsSchemaList({
    filters: customEventsSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: customEventsSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    rows: 50,
    sortKey: "name",
  });

  const {
    infiniteData: customEventSchemaList,
    anyElementsToFetch: anyCustomEventSchemaToFetch,
  } = useManageInfiniteScroll(infiniteCustomEventSchemaList || []);

  const isSchemaAllowed = ({ schema }: { schema: SchemaFields }) => {
    const allowedFieldTypes = [
      "boolean",
      "datetime",
      "number",
      "text",
      "collection",
      "object",
    ];
    return schema?.fields?.every(({ type }: SchemaAttribute) =>
      allowedFieldTypes.includes(type),
    );
  };
  const allowedSchemas = customEventSchemaList.filter(isSchemaAllowed) || [];

  const initialValue = selectedSchemaDetails && {
    id: selectedSchemaDetails.eventType as string,
    label: selectedSchemaDetails.name,
  };

  return (
    <Grid item xs={4} mt={12}>
      <SelectInput
        displaySearch
        data-test-id="campaign-trigger-external-event-search"
        width="100%"
        items={
          allowedSchemas.map((event) => ({
            label: event.name,
            id: event.eventType,
          })) || []
        }
        label={
          <RequiredFieldLabel
            text={message({
              id: "eventName",
            })}
          />
        }
        name="event"
        asyncSearch={(searchedQuery: string) => setSearchEvent(searchedQuery)}
        initialValue={initialValue}
        placeholder={message({ id: "eventName" })}
        anyElementsToFetch={anyCustomEventSchemaToFetch}
        loadMoreElements={() =>
          !isValidating && setCustomEventSchemaSize(customEventSchemaSize + 1)
        }
        disabled={isEditMode}
      />
    </Grid>
  );
});
